/** Domaine racine par environnement et provider (aws par défaut, s3ns = cloud souverain). */
const DOMAIN_BY_ENV: Record<string, Record<string, string>> = {
  aws: {
    development: "stonal-dev.io",
    test: "stonal-test.io",
    staging: "stonal-staging.io",
    production: "stonal.io",
  },
  s3ns: {
    development: "stonal-secnum-dev.io",
    production: "stonal-secnum.io",
  },
};

/** Détecte le provider à partir du hostname (S3NS = stonal-secnum*). */
function getProviderFromHostname(hostname: string): string {
  if (hostname.includes("stonal-secnum")) return "s3ns";
  return "aws";
}

/** Détecte l'environnement à partir du hostname (ex. edit.stonal-test.io → test). */
function getEnvFromHostname(hostname: string): string {
  if (hostname.includes("stonal-dev") || hostname.includes("stonal-secnum-dev")) return "development";
  if (hostname === "localhost" || hostname.startsWith("127.")) return "development";
  if (hostname.includes("stonal-test")) return "test";
  if (hostname.includes("stonal-staging")) return "staging";
  return "production";
}

/**
 * Extrait la stack depuis le premier label du hostname (ex. edit-unofi.stonal.io → "unofi").
 * Seul prod porte une stack tenant ; dev/test/staging et la stack principale prod renvoient "".
 */
function getStackFromHostname(hostname: string): string {
  const firstLabel = hostname.split(".")[0] ?? "";
  const dashIndex = firstLabel.indexOf("-");
  return dashIndex === -1 ? "" : firstLabel.slice(dashIndex + 1);
}

/**
 * URL de base de l'app "edit" (nouvelle ergonomie).
 * Détection automatique via le hostname courant :
 *   edit.stonal-test.io           → app.stonal-test.io
 *   edit.stonal.io                → app.stonal.io
 *   edit-unofi.stonal.io          → app-unofi.stonal.io
 *   edit.stonal-secnum.io         → app.stonal-secnum.io        (S3NS)
 *   edit-unofi.stonal-secnum.io   → app-unofi.stonal-secnum.io  (S3NS tenant)
 * - REACT_APP_EDIT_APP_BASE_URL : override explicite (optionnel).
 * - REACT_APP_ENV : force l’env (optionnel, sinon déduit du hostname).
 * - REACT_APP_STACK : force la stack (optionnel, sinon déduite du hostname).
 * - REACT_APP_PROVIDER : force le provider aws/s3ns (optionnel, sinon déduit du hostname).
 */
export const getEditAppBaseUrl = (): string => {
  if (process.env.REACT_APP_EDIT_APP_BASE_URL) {
    return process.env.REACT_APP_EDIT_APP_BASE_URL;
  }
  if (typeof window === "undefined") {
    return `https://app.${DOMAIN_BY_ENV.aws.production}`;
  }
  const hostname = window.location.hostname;
  const env = process.env.REACT_APP_ENV ?? getEnvFromHostname(hostname);
  const provider = process.env.REACT_APP_PROVIDER ?? getProviderFromHostname(hostname);
  const domainsForProvider = DOMAIN_BY_ENV[provider] ?? DOMAIN_BY_ENV.aws;
  const domain = domainsForProvider[env] ?? domainsForProvider.production ?? DOMAIN_BY_ENV.aws.production;
  const stack = process.env.REACT_APP_STACK ?? getStackFromHostname(hostname);
  const subdomain = env === "production" && stack ? `app-${stack}` : "app";
  return `https://${subdomain}.${domain}`;
};
